/**
 * Deterministic PRNG for seed generation.
 *
 * `mulberry32` is a small, fast 32-bit hash-based generator with good
 * statistical properties for non-cryptographic use. Two runs starting with
 * the same seed produce IDENTICAL output — which is exactly what we want
 * for mock-data reproducibility (tests can assert specific values; the UI
 * snapshot is stable across reloads).
 *
 * Do NOT use this for anything that touches real capital or auth — it is
 * trivially predictable. Real randomness goes through `globalThis.crypto`.
 */

export interface Rng {
  /** Float in [0, 1). */
  random: () => number;
  /** Integer in [min, max) — `max` exclusive. */
  int: (min: number, max: number) => number;
  /** Pick one element. Throws on empty. */
  pick: <T>(xs: readonly T[]) => T;
  /** Pick `n` distinct elements without replacement (Fisher–Yates partial). */
  sample: <T>(xs: readonly T[], n: number) => T[];
  /** True with probability `p` (in [0, 1]). */
  chance: (p: number) => boolean;
  /** Shuffle a copy (does not mutate input). */
  shuffle: <T>(xs: readonly T[]) => T[];
  /** Normal-ish distribution via Box-Muller, centred on `mean`, sd `sd`. */
  gauss: (mean: number, sd: number) => number;
}

export const DEFAULT_SEED = 0xdeadbeef;

export const makeRng = (seed: number = DEFAULT_SEED): Rng => {
  let s = seed >>> 0;
  const random = (): number => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };

  const int = (min: number, max: number): number => {
    if (max <= min) {
      return min;
    }
    return Math.floor(random() * (max - min)) + min;
  };

  const pick = <T>(xs: readonly T[]): T => {
    if (xs.length === 0) {
      throw new RangeError('rng.pick: empty array');
    }
    const v = xs[int(0, xs.length)];
    if (v === undefined) {
      throw new RangeError('rng.pick: unexpected undefined');
    }
    return v;
  };

  const shuffle = <T>(xs: readonly T[]): T[] => {
    const out = xs.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = int(0, i + 1);
      const tmp = out[i] as T;
      out[i] = out[j] as T;
      out[j] = tmp;
    }
    return out;
  };

  const sample = <T>(xs: readonly T[], n: number): T[] => {
    if (n >= xs.length) {
      return shuffle(xs);
    }
    return shuffle(xs).slice(0, Math.max(0, n));
  };

  const chance = (p: number): boolean => random() < p;

  const gauss = (mean: number, sd: number): number => {
    // Box-Muller
    const u1 = Math.max(random(), 1e-9);
    const u2 = random();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return mean + sd * z;
  };

  return { random, int, pick, sample, chance, shuffle, gauss };
};
